/**
 * The five configuration documents of a new exploration, owned by the page.
 *
 * They follow the draft's choices: a different objective reloads the
 * exploration config, a different laser its design space, a different
 * calibration its colour constants, the header choice lands in the plate
 * design. The plate design and the width measurement start from the generic
 * templates. The setup cards edit the documents through the setters; the
 * start section posts them as they are.
 *
 * `edited` names the documents a card changed (for the review summaries);
 * a reload of a document (new objective, new laser) clears its flag.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { parseConfigField, preprocessJson } from "../../../helper/explorationConfigHelpers";

// The laser's design-space config name comes from the LaserDevice row (set at
// laser import); this map only covers legacy rows created before
// parameters_config_name existed.
export const LEGACY_LASER_CONFIGS = {
  IPG2020: "parameters_ipg",
  ipg: "parameters_ipg",
  Trumpf5050: "parameters_trumpf5050",
  trump500: "parameters_trumpf5050",
  "Carbide-CB3-40W": "parameters_carbide_cb3_40w",
  "Pharos-PH2": "parameters_pharos_ph2",
};

export const OBJECTIVE_CONFIGS = {
  white_exploration: "white_exploration_configuration",
  black_exploration: "black_exploration_configuration",
  chromatic_exploration: "color_exploration_configuration_generic",
};

export const CONFIG_LABELS = {
  exploration_config: "Optimization",
  laser_config: "Laser",
  plate_config: "Plate design",
  width_measurement_config: "Width measurement",
  color_measurement_config: "Color measurement",
};

const validUuidLike = (id) => id && id !== "null" && id !== "undefined" && String(id).length > 0;

async function fetchConfig(name) {
  const res = await axios.get("api/configs/?name=" + name);
  if (res.status !== 200 || !res.data?.length) return null;
  return preprocessJson(res.data[0].config_json);
}

export default function useExplorationConfigs(draft) {
  const [explorationConfig, setExplorationConfigState] = useState({});
  const [laserConfig, setLaserConfigState] = useState({});
  const [plateDesignConfig, setPlateDesignConfigState] = useState({});
  const [widthMeasurementConfig, setWidthMeasurementConfigState] = useState({});
  const [colorMeasurementConfig, setColorMeasurementConfig] = useState({});
  const [edited, setEdited] = useState([]);

  const markEdited = useCallback(
    (key) => setEdited((current) => (current.includes(key) ? current : [...current, key])),
    []
  );
  const unmarkEdited = useCallback(
    (key) => setEdited((current) => current.filter((item) => item !== key)),
    []
  );

  const setExplorationConfig = useCallback(
    (next) => {
      setExplorationConfigState(next);
      markEdited("exploration_config");
    },
    [markEdited]
  );
  const setLaserConfig = useCallback(
    (next) => {
      setLaserConfigState(next);
      markEdited("laser_config");
    },
    [markEdited]
  );
  const setPlateDesignConfig = useCallback(
    (next) => {
      setPlateDesignConfigState(next);
      markEdited("plate_config");
    },
    [markEdited]
  );
  const setWidthMeasurementConfig = useCallback(
    (next) => {
      setWidthMeasurementConfigState(next);
      markEdited("width_measurement_config");
    },
    [markEdited]
  );

  // Width measurement: the generic template (the backend's default too).
  useEffect(() => {
    let cancelled = false;
    fetchConfig("width_measurement_generic")
      .then((config) => {
        if (!cancelled && config) setWidthMeasurementConfigState(config);
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Colour measurement: the chosen calibration's constants, else the generic template.
  useEffect(() => {
    let cancelled = false;
    const camId = draft.cameraCalibrationId;
    const load = async () => {
      if (validUuidLike(camId)) {
        try {
          const res = await axios.get(
            `api/color/explorer/color/calibration/?id=${encodeURIComponent(camId)}`
          );
          if (cancelled) return;
          if (res.status === 200 && res.data?.length > 0) {
            const cfg = parseConfigField(res.data[0].colorimetric_calibration_config);
            if (cfg && typeof cfg === "object") {
              setColorMeasurementConfig(preprocessJson(cfg));
              return;
            }
          }
        } catch (err) {
          console.error(err);
        }
      }
      try {
        const config = await fetchConfig("color_measurement_generic");
        if (!cancelled && config) setColorMeasurementConfig(config);
      } catch (err) {
        console.error(err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [draft.cameraCalibrationId]);

  // Plate design: the generic template once, with the current header choice;
  // later header changes land in the loaded document.
  const markHeaderRef = useRef(draft.markHeader);
  markHeaderRef.current = draft.markHeader;
  useEffect(() => {
    let cancelled = false;
    fetchConfig("plate_design_generic")
      .then((config) => {
        if (cancelled || !config) return;
        setPlateDesignConfigState({
          ...config,
          header: { ...(config.header || {}), mark_header: markHeaderRef.current },
        });
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    setPlateDesignConfigState((current) =>
      current && Object.keys(current).length > 0
        ? { ...current, header: { ...(current.header || {}), mark_header: draft.markHeader } }
        : current
    );
  }, [draft.markHeader]);

  // The laser's design space follows the chosen laser.
  useEffect(() => {
    const name = draft.laserConfigName || LEGACY_LASER_CONFIGS[draft.laserName] || null;
    if (!name) {
      setLaserConfigState({});
      return undefined;
    }
    let cancelled = false;
    fetchConfig(name)
      .then((config) => {
        if (cancelled || !config) return;
        setLaserConfigState(config);
        unmarkEdited("laser_config");
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [draft.laserConfigName, draft.laserName, unmarkEdited]);

  // The exploration config follows the chosen optimization preset (objective row).
  useEffect(() => {
    const name = OBJECTIVE_CONFIGS[draft.objectiveName] || null;
    if (!name) {
      setExplorationConfigState({});
      return undefined;
    }
    let cancelled = false;
    fetchConfig(name)
      .then((config) => {
        if (cancelled || !config) return;
        setExplorationConfigState(config);
        unmarkEdited("exploration_config");
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [draft.objectiveName, unmarkEdited]);

  return {
    explorationConfig,
    laserConfig,
    plateDesignConfig,
    widthMeasurementConfig,
    colorMeasurementConfig,
    setExplorationConfig,
    setLaserConfig,
    setPlateDesignConfig,
    setWidthMeasurementConfig,
    edited,
  };
}
